import { useEffect, useMemo, useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import { useHistory } from "react-router-dom";
import { AlertTriangle, CalendarDays, Clock3, Sparkles } from "lucide-react";
import {
  createExam,
  createFlashcardSession,
  createPracticeSession,
  getMyStudyPlan,
  startStudyPlanTask,
} from "utils/supabase";
import { linkStudyPlanTask } from "utils/studyPlanLink";

function getNextTodayTask(tasks = []) {
  const ordered = [...tasks].sort(
    (a, b) => Number(a.task_order ?? 999) - Number(b.task_order ?? 999)
  );

  const inProgress = ordered.find((task) => task.status === "in_progress");
  if (inProgress) return inProgress;

  return ordered.find((task) => task.status === "pending") || ordered[0] || null;
}

function getTaskTypeLabel(type) {
  const labels = {
    practice: "Práctica",
    incorrect_review: "Repaso de errores",
    flashcards: "Flashcards",
    simulator: "Simulador",
    light_review: "Repaso ligero",
  };

  return labels[type] || "Actividad";
}

function formatExamDate(dateLike) {
  if (!dateLike) return "";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
}

function isExamDatePast(dateLike) {
  if (!dateLike) return false;
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return false;

  const examDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return examDay.getTime() < today.getTime();
}

export default function CardPlanEstudio({ loading = false }) {
  const history = useHistory();
  const [planSummary, setPlanSummary] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isStartingTask, setIsStartingTask] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function fetchPlanSummary() {
      try {
        setIsFetching(true);
        const { data, error } = await getMyStudyPlan();

        if (!mounted) return;

        if (error) {
          console.error("Error fetching study plan summary:", error);
          setPlanSummary(null);
          return;
        }

        setPlanSummary(data || null);
      } catch (error) {
        console.error("Unexpected error fetching study plan summary:", error);
        setPlanSummary(null);
      } finally {
        if (mounted) {
          setIsFetching(false);
        }
      }
    }

    fetchPlanSummary();

    return () => {
      mounted = false;
    };
  }, []);

  const showSkeleton = loading || isFetching;

  const nextTask = useMemo(() => {
    const tasks = Array.isArray(planSummary?.today?.tasks) ? planSummary.today.tasks : [];
    return getNextTodayTask(tasks);
  }, [planSummary]);

  const todayTasks = Array.isArray(planSummary?.today?.tasks) ? planSummary.today.tasks : [];
  const daysUntilExam = Number(planSummary?.plan?.days_until_exam ?? 0);
  const dailyMinutes = Number(planSummary?.plan?.daily_minutes ?? 0);
  const phase = planSummary?.today?.phase || "Cobertura";
  const completion = Math.round(Number(planSummary?.today?.completion_percentage ?? 0));
  const isRestDay = planSummary?.today?.is_study_day === false;
  const isDayCompleted =
    completion >= 100 ||
    (todayTasks.length > 0 && todayTasks.every((task) => task.status === "completed"));
  const nextTaskLabel = nextTask?.specialty_name || "Tu ruta de estudio";
  const nextTaskTypeLabel = nextTask ? getTaskTypeLabel(nextTask.type) : "Preparación";
  const examDatePassed = Boolean(planSummary?.has_plan) && isExamDatePast(planSummary?.plan?.exam_date);

  const handleGoPlan = () => {
    history.push("/admin/plan-estudio");
  };

  const handleStartTask = async () => {
    if (!nextTask || isStartingTask || isRestDay) return;

    try {
      setIsStartingTask(true);
      const resultData = nextTask.result_data || {};

      if (nextTask.type === "simulator") {
        const existingExamId = resultData.exam_id;
        if (existingExamId) {
          history.push(`/admin/simulador/${existingExamId}`);
          return;
        }

        const specialtyIds = [nextTask.specialty_id].filter(Boolean);
        const totalQuestions = Number(nextTask.target_count || 40);
        const { exam } = await createExam({
          selectedSpecialtyIds: specialtyIds,
          totalQuestions,
          casePercentage: 0.4,
          difficultyFilter: nextTask.metadata?.difficulty || "false",
          durationMinutes: Math.max(30, Math.round(totalQuestions * 1.2)),
          modo: "plan_estudio",
          studyPlanTaskId: nextTask.id,
        });

        await startStudyPlanTask(nextTask.id, { exam_id: exam.id });
        linkStudyPlanTask("exam", exam.id, nextTask.id);
        history.push(`/admin/simulador/${exam.id}`);
        return;
      }

      if (nextTask.type === "practice" || nextTask.type === "incorrect_review") {
        const existingSessionId = resultData.study_session_id;
        if (existingSessionId) {
          history.push(`/admin/practica/${existingSessionId}`);
          return;
        }

        const specialtyIds = [nextTask.specialty_id].filter(Boolean);
        const { data, error } = await createPracticeSession(
          specialtyIds,
          Number(nextTask.target_count || 0),
          null,
          false,
          false,
          nextTask.type === "incorrect_review",
          40
        );

        if (error) throw error;

        const session = Array.isArray(data) ? data[0] : data;
        const sessionId = session?.study_session_id || session?.session_id || session?.id;
        if (!sessionId) throw new Error("No se pudo crear la sesión de práctica.");

        await startStudyPlanTask(nextTask.id, { study_session_id: sessionId });
        linkStudyPlanTask("practice", sessionId, nextTask.id);
        history.push(`/admin/practica/${sessionId}`);
        return;
      }

      if (nextTask.type === "flashcards") {
        const existingSessionId = resultData.flashcard_session_id;
        if (existingSessionId) {
          history.push(`/admin/flashcards/${existingSessionId}`);
          return;
        }

        const { data, error } = await createFlashcardSession(
          [nextTask.specialty_id].filter(Boolean),
          Number(nextTask.target_count || 20),
          nextTask.metadata?.preferred_mode || "due"
        );

        if (error) throw error;

        const sessionId = data?.flashcard_session_id || data?.[0]?.flashcard_session_id || data?.id;
        if (!sessionId) throw new Error("No se pudo crear la sesión de flashcards.");

        await startStudyPlanTask(nextTask.id, { flashcard_session_id: sessionId });
        linkStudyPlanTask("flashcards", sessionId, nextTask.id);
        history.push(`/admin/flashcards/${sessionId}`);
        return;
      }

      history.push("/admin/plan-estudio");
    } catch (error) {
      console.error("Error iniciando actividad del plan:", error);
      history.push("/admin/plan-estudio");
    } finally {
      setIsStartingTask(false);
    }
  };

  if (showSkeleton) {
    return (
      <div className="dashboard-feature-card relative w-full mb-6 rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 space-y-2 md:flex-1">
            <Skeleton variant="text" width="65%" height={42} animation="wave" />
            <Skeleton variant="text" width="45%" height={22} animation="wave" />
          </div>
          <Skeleton variant="circular" width={72} height={72} animation="wave" />
        </div>
        <Skeleton variant="rounded" width="100%" height={52} animation="wave" sx={{ mt: 2 }} />
      </div>
    );
  }

  // La fecha del ENARM ya paso: priorizamos que el usuario actualice su plan antes de mostrar datos obsoletos.
  if (examDatePassed) {
    return (
      <div className="dashboard-feature-card relative mb-3 w-full overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm md:mb-6">
        <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-500 px-3 py-3 text-white md:px-4 md:py-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="mt-1 text-lg font-bold leading-tight md:text-2xl">Mi Plan ENARM</h4>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white bg-opacity-20">
              <AlertTriangle size={22} />
            </div>
          </div>
        </div>

        <div className="p-3 md:p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-left">
              <p className="mt-1 text-lg font-bold text-blueGray-900">Actualiza tu preparación</p>
            </div>

            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
              Desactualizado
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-blueGray-600">
            <div className="rounded-xl bg-blueGray-50 px-2.5 py-2.5">
              <div className="flex items-center gap-1.5 text-blueGray-500">
                <Clock3 size={14} />
                <span>Tiempo</span>
              </div>
              <div className="mt-1 font-semibold text-blueGray-800">Personalizado</div>
            </div>
            <div className="rounded-xl bg-blueGray-50 px-2.5 py-2.5">
              <div className="flex items-center gap-1.5 text-blueGray-500">
                <Sparkles size={14} />
                <span>Fase</span>
              </div>
              <div className="mt-1 font-semibold uppercase text-blueGray-800">Sin plan</div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700">Fecha vencida</p>
            <div className="mt-1 min-w-0">
              <p className="truncate text-sm font-semibold text-blueGray-800">
                La fecha de tu ENARM ya pasó.
              </p>
              <p className="mt-1 text-sm font-small uppercase tracking-wide text-blueGray-500">
                Actualiza tu plan para continuar
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoPlan}
            className="relative z-10 mt-4 w-full rounded-xl border border-indigo-700 bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700 focus:outline-none focus:ring-0 active:outline-none md:px-6 md:py-3 md:text-base"
          >
            Actualizar mi plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-feature-card relative mb-3 w-full overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm md:mb-6">
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-500 px-3 py-3 text-white md:px-4 md:py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="mt-1 text-lg font-bold leading-tight md:text-2xl">Mi Plan ENARM</h4>
            {planSummary?.has_plan && planSummary?.plan?.exam_date ? (
              <p className="mt-0.5 text-xs font-medium text-indigo-100">
                Examen: {formatExamDate(planSummary.plan.exam_date)}
              </p>
            ) : null}
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white bg-opacity-20">
            <CalendarDays size={22} />
          </div>
        </div>
      </div>

      <div className="p-3 md:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-left">
            <p className="mt-1 text-lg font-bold text-blueGray-900">
              {planSummary?.has_plan ? `Faltan ${daysUntilExam} días para el ENARM` : "Organiza tu preparación"}
            </p>
          </div>

          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            {planSummary?.has_plan ? (isRestDay ? "Descanso" : "Activo") : "Nuevo"}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-blueGray-600">
          <div className="rounded-xl bg-blueGray-50 px-2.5 py-2.5">
            <div className="flex items-center gap-1.5 text-blueGray-500">
              <Clock3 size={14} />
              <span>Tiempo</span>
            </div>
            <div className="mt-1 font-semibold text-blueGray-800">
              {planSummary?.has_plan ? `${dailyMinutes} min` : "Personalizado"}
            </div>
          </div>
          <div className="rounded-xl bg-blueGray-50 px-2.5 py-2.5">
            <div className="flex items-center gap-1.5 text-blueGray-500">
              <Sparkles size={14} />
              <span>Fase</span>
            </div>
            <div className="mt-1 font-semibold uppercase text-blueGray-800">
              {planSummary?.has_plan ? phase : "Sin plan"}
            </div>
          </div>
        </div>

        {!isRestDay && (
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-widest text-blueGray-500">
              <span>Progreso del día</span>
              <span>{completion}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-blueGray-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                style={{ width: `${Math.min(100, Math.max(0, completion))}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-700">
            {isRestDay ? "Día de descanso" : "Siguiente actividad"}
          </p>
          <div className="mt-1 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-blueGray-800">
                {isRestDay
                  ? "Hoy no tienes actividades programadas."
                  : planSummary?.has_plan
                    ? nextTaskLabel
                    : "Crea tu plan y ENARM Apex te indicará qué estudiar cada día."}
              </p>
              <p className="mt-1 text-sm font-small uppercase tracking-wide text-blueGray-500">
                {isRestDay ? "Tómate el día para descansar" : planSummary?.has_plan ? nextTaskTypeLabel : "Tu ruta"}
              </p>
            </div>
            {planSummary?.has_plan && nextTask && !isRestDay && isDayCompleted ? (
              <span className="flex-shrink-0 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                Completado
              </span>
            ) : planSummary?.has_plan && nextTask && !isRestDay && !isDayCompleted ? (
              <button
                type="button"
                onClick={handleStartTask}
                disabled={isStartingTask}
                className="flex-shrink-0 rounded-xl bg-indigo-100 px-3 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isStartingTask
                  ? "Cargando..."
                  : nextTask.status === "in_progress"
                    ? "Continuar"
                    : "Comenzar"}
              </button>
            ) : null}
          </div>
        </div>

        {isRestDay ? (
          <div className="relative z-10 mt-4 flex w-full items-center justify-center rounded-xl border border-blueGray-200 bg-blueGray-100 px-3 py-2.5 text-sm font-semibold text-blueGray-600 shadow-sm md:px-6 md:py-3 md:text-base">
            Día de descanso
          </div>
        ) : completion >= 100 ? (
          <button
            type="button"
            onClick={handleGoPlan}
            className="relative z-10 mt-4 w-full rounded-xl border border-indigo-700 bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700 focus:outline-none focus:ring-0 active:outline-none md:px-6 md:py-3 md:text-base"
          >
            Ver mi plan
          </button>
        ) : (
          <button
            type="button"
            onClick={handleGoPlan}
            className="relative z-10 mt-4 w-full rounded-xl border border-indigo-700 bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700 focus:outline-none focus:ring-0 active:outline-none md:px-6 md:py-3 md:text-base"
          >
            {planSummary?.has_plan ? "Ver mi plan" : "Crear mi plan"}
          </button>
        )}
      </div>
    </div>
  );
}
